import React from 'react';
import TrendsChart from './TrendsChart';
import { useSpotlight } from '../../hooks/useSpotlight';

/** Tag pills — 12.5px on #292E31, dot colour set per card. */
const Pills: React.FC<{ items: string[]; dot: string }> = ({ items, dot }) => (
    <div className="flex flex-wrap gap-2">
        {items.map((item) => (
            <span
                key={item}
                className="inline-flex items-center gap-[7px] rounded-full bg-dark-gunmetal px-[13px] py-[7px] text-[12.5px] font-medium text-dark-textSecondary"
            >
                <span className="h-[5px] w-[5px] rounded-full" style={{ background: dot }} />
                {item}
            </span>
        ))}
    </div>
);

const Icon: React.FC<{ src: string }> = ({ src }) => (
    <span className="mb-5 inline-flex">
        <img src={src} alt="" aria-hidden="true" className="h-[46px] w-[46px]" />
    </span>
);

/** Secondary card: lifts 4px on hover and carries the pointer spotlight. */
const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className = '',
}) => {
    const attachSpotlight = useSpotlight<HTMLElement>('#303539');
    return (
        <article
            ref={attachSpotlight}
            className={`relative overflow-hidden rounded-2xl border-[0.8px] border-dark-gunmetal bg-[#303539] p-[clamp(24px,3vw,32px)] transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-white/[0.24] ${className}`}
        >
            {children}
        </article>
    );
};

const FeaturesSection: React.FC = () => {
    return (
        <section
            id="features"
            className="relative z-[1] scroll-mt-24 border-t border-dark-gunmetal px-5 py-[clamp(64px,9vw,120px)]"
        >
            <div className="mx-auto max-w-[1180px]">
                <div className="flex flex-wrap items-end justify-between gap-5">
                    <div className="max-w-[660px]">
                        <span className="text-[10px] font-medium uppercase tracking-[0.16em] text-success">
                            What Fork does best
                        </span>
                        <h2 className="mt-3.5 text-[clamp(28px,3.8vw,48px)] font-extrabold leading-[1.04] tracking-[-0.035em] text-white [text-wrap:balance]">
                            Built for the way you really eat
                        </h2>
                    </div>
                    <p className="max-w-[36ch] text-[15.5px] leading-[1.6] text-dark-textSecondary">
                        Photo-first tracking with guidance you can act on — the product we use
                        ourselves, every day.
                    </p>
                </div>

                <div className="mt-[clamp(28px,4vw,48px)] grid gap-3 lg:grid-cols-2">
                    {/* hero feature — full-bleed photo, content bottom-aligned */}
                    <article className="relative grid min-h-[420px] content-end overflow-hidden rounded-2xl border-[0.8px] border-dark-gunmetal bg-[#303539] transition-colors duration-300 hover:border-white/[0.22]">
                        <img
                            src="/assets/food/meal-4.webp"
                            alt=""
                            aria-hidden="true"
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover opacity-90"
                        />
                        <span
                            aria-hidden="true"
                            className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,19,22,0.10)_0%,rgba(15,19,22,0.80)_52%,rgba(15,19,22,0.97)_100%)]"
                        />
                        <div className="relative p-[clamp(24px,3vw,34px)]">
                            <Icon src="/assets/app/calories.png" />
                            <h3 className="mb-3 text-[clamp(22px,2.6vw,30px)] font-bold tracking-[-0.025em] text-white">
                                Instant photo analysis
                            </h3>
                            <p className="mb-5 max-w-[42ch] text-[16px] leading-[1.6] text-dark-textSecondary">
                                Identify what is on the plate, estimate portions, and break down macros
                                from a single snap.
                            </p>
                            <Pills
                                dot="#FFD6D7"
                                items={['Multi-food photos', 'Portion-aware macros', 'Voice capture']}
                            />
                        </div>
                    </article>

                    <div className="grid min-w-0 gap-3">
                        <Card>
                            <Icon src="/assets/app/fiber.png" />
                            <h3 className="mb-3 text-[clamp(21px,2.4vw,27px)] font-bold tracking-[-0.025em] text-white">
                                Nutrition scoring that teaches
                            </h3>
                            <p className="mb-[18px] text-[16px] leading-[1.6] text-dark-textSecondary">
                                Each meal gets a clear score with notes on balance, protein, fiber, and
                                where to improve next time.
                            </p>
                            <Pills
                                dot="#E2FFEE"
                                items={[
                                    'Meal-level score',
                                    'Why it is off balance',
                                    'Realistic suggestions',
                                ]}
                            />
                            <div className="mt-5 overflow-hidden rounded-2xl bg-dark-base">
                                <img
                                    src="/assets/step-3.webp"
                                    alt="Meal detail with calories, macros and meal score"
                                    loading="lazy"
                                    className="h-[190px] w-full object-cover"
                                    style={{ objectPosition: 'center 22%' }}
                                />
                            </div>
                        </Card>

                        <Card>
                            <Icon src="/assets/app/fat.png" />
                            <h3 className="mb-3 text-[clamp(21px,2.4vw,27px)] font-bold tracking-[-0.025em] text-white">
                                Built for busy moments
                            </h3>
                            <p className="mb-[18px] text-[16px] leading-[1.6] text-dark-textSecondary">
                                Log from Apple Watch, dictate a meal, or quickly review today's scores
                                without digging through menus.
                            </p>
                            <Pills
                                dot="#F1DFFF"
                                items={[
                                    'Apple Watch logging',
                                    'Hands-free voice',
                                    'Tuned for daily use',
                                ]}
                            />
                        </Card>
                    </div>
                </div>

                {/* trends feature — full width, no hover lift in v1 */}
                <TrendsFeature />
            </div>
        </section>
    );
};

/** Wide trends card: copy on the left, interactive chart on the right. */
const TrendsFeature: React.FC = () => {
    const attachSpotlight = useSpotlight<HTMLElement>('#303539');
    return (
        <article
            ref={attachSpotlight}
            className="relative mt-3 grid items-center gap-[clamp(24px,4vw,52px)] overflow-hidden rounded-2xl border-[0.8px] border-dark-gunmetal bg-[#303539] p-[clamp(24px,3vw,36px)] lg:grid-cols-2"
        >
            <div className="min-w-0">
                <Icon src="/assets/app/carbohydrates.png" />
                <h3 className="mb-3 text-[clamp(21px,2.4vw,27px)] font-bold tracking-[-0.025em] text-white">
                    Progress you can act on
                </h3>
                <p className="mb-[18px] text-[16px] leading-[1.6] text-dark-textSecondary">
                    Spot patterns in protein, carbs, and overall balance so you know what to adjust
                    next week.
                </p>
                <Pills
                    dot="#F9FFCC"
                    items={['Weekly & monthly trends', 'Habit streaks', 'Coach-style summaries']}
                />
            </div>

            <TrendsChart />
        </article>
    );
};

export default FeaturesSection;
